/**
 * Pure monthly cash-flow resolver for the Properties section.
 *
 * Temporal-integrity model (see PROPERTY_CASHFLOW_PLAN.md §1A):
 *   - The CURRENT month is computed live from the mutable inputs
 *     (`property_setting` + the active loan + rental_listings).
 *   - PAST months are read from frozen `property_log` snapshots and never
 *     recomputed — editing an assumption today cannot rewrite them.
 *   - FUTURE months are NOT computed at all.
 *
 * Both a live `DealSettingsRow` and a frozen `property_log` row normalize into
 * the same `MonthlyInputs`, so one `computeMonthBreakdown` serves both.
 *
 * All functions here are deterministic and free of React / DB / `Date.now`
 * dependencies — callers pass the reference month in explicitly.
 */
import { amortizedEmi, interestOnlyMonthly, termInMonths } from "@/lib/calculators";
import type {
  CashflowOverrides,
  LoanInputs,
  Strategy,
} from "@/types/pipeline";

/**
 * Structural subset of the composed `property_setting` row that the cash-flow
 * resolver reads. Decoupled from the full `DealSettingsRow` so both the raw row
 * and the defaults-merged `useDealSettings().settings` object satisfy it.
 */
export interface SettingsForCashflow {
  purchase_price?: number | null;
  after_repair_value?: number | null;
  monthly_rent?: number | null;
  monthly_cash_flow?: CashflowOverrides | null;
  flip?: {
    hard_money?: LoanInputs;
    /** Monthly carrying costs (taxes+ins · utilities) — the flip holding cost. */
    taxes_monthly?: number;
    utilities_monthly?: number;
  } | null;
  brrrr?: { hard_money?: LoanInputs; long_term?: LoanInputs } | null;
  rental?: { long_term?: LoanInputs } | null;
}

/** Which `property_setting` financing scenario is active for a month. */
export type LoanScenario =
  | "brrrr_hard_money"
  | "brrrr_long_term"
  | "flip"
  | "rental";

/** Financing metadata from the `loans` switcher row. */
export interface LoanSwitch {
  loan_type: string | null;
  is_enabled: boolean | null;
}

/** Normalized inputs for a single month — the shared shape log + live feed into. */
export interface MonthlyInputs {
  strategy: Strategy;
  /** null = held all-cash (no debt service). */
  loanType: LoanScenario | null;
  purchase: number;
  arv: number;
  /** Gross monthly rent in effect that month. */
  rent: number;
  /** Per-line monthly overrides (property_setting.monthly_cash_flow). */
  cashflow: CashflowOverrides;
  /** Resolved active loan terms, or null when all-cash. */
  loan: LoanInputs | null;
  /**
   * Whether the loan had started (enabled + `loan_date` ≤ this month). When
   * false, the month carries no debt service — the loan hadn't begun yet.
   */
  loanStarted: boolean;
  /**
   * Flip carrying-cost estimate for THIS month (taxes+ins · utilities), sourced
   * per-month — from live settings for the current month, from the frozen
   * `property_log.flip_holding_cost` for past months. Null for non-flip months.
   */
  flipHolding?: { taxes: number; utilities: number } | null;
}

export interface CashflowLine {
  key: string;
  label: string;
  amount: number;
}

/** One month's full breakdown. */
export interface MonthBreakdown {
  income: { gross: number; other: number; egi: number };
  opex: { lines: CashflowLine[]; total: number };
  /** Monthly debt service (EMI / interest-only carry). */
  debt: number;
  /** egi − opex.total − debt. */
  net: number;
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Month-index helpers (year * 12 + monthIndex0). UTC, no Date objects stored.
 * ────────────────────────────────────────────────────────────────────────── */

/** `year`/`month` (month 1-12) → comparable integer index. */
export function monthKey(year: number, month1to12: number): number {
  return year * 12 + (month1to12 - 1);
}

/** Current month index in UTC. `now` is injectable for deterministic tests. */
export function currentMonthKeyUTC(now: Date = new Date()): number {
  return now.getUTCFullYear() * 12 + now.getUTCMonth();
}

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

export const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/* ────────────────────────────────────────────────────────────────────────── *
 * Loan-scenario resolution (mirrors the snapshot SQL in
 * 20260616000100_property_log_snapshot_fn.sql so live + frozen agree)
 * ────────────────────────────────────────────────────────────────────────── */

export function resolveLoanScenario(
  strategy: Strategy,
  loan: LoanSwitch | null,
): LoanScenario | null {
  if (strategy === "Flip") return "flip";
  if (strategy === "Rental") return "rental";
  // BRRRR: the loans switcher decides hard-money (short) vs long-term (refi).
  if (!loan || loan.is_enabled !== true) return null; // all-cash
  return loan.loan_type === "hard_money" ? "brrrr_hard_money" : "brrrr_long_term";
}

/**
 * Monthly debt service for the resolved scenario.
 *   - brrrr_long_term → amortized refi on ARV × LTV, term in YEARS
 *   - rental          → amortized loan on purchase × LTV, term in YEARS
 *   - flip / brrrr_hard_money → INTEREST-ONLY carry on purchase × LTV
 *
 * Hard money is not amortized (`loanViewMeta().isAmortizing === false`): the
 * borrower pays interest each month and balloons the whole principal at
 * sale/refi. Amortizing it over the term would charge principal twice — once
 * spread across the carry months, then again as the payoff at close — and would
 * report a ~10× monthly carry on a 12-month term.
 */
export function monthlyDebtService(
  loanType: LoanScenario | null,
  loan: LoanInputs | null,
  purchase: number,
  arv: number,
): number {
  if (!loanType || !loan) return 0;
  const ltv = Number(loan.ltv) || 0;
  const rate = Number(loan.rate) || 0;
  if (loanType === "brrrr_long_term") {
    return amortizedEmi((arv * ltv) / 100, rate, termInMonths(loan, "years"));
  }
  if (loanType === "rental") {
    return amortizedEmi((purchase * ltv) / 100, rate, termInMonths(loan, "years"));
  }
  // flip | brrrr_hard_money — interest-only carry, principal ballooned at exit
  return interestOnlyMonthly((purchase * ltv) / 100, rate);
}

/**
 * Monthly mortgage INTEREST (not full debt service) for the resolved scenario —
 * the deductible portion that lands on Schedule E line 12. Draws on the same
 * scenario principal as {@link monthlyDebtService} (refi on ARV×LTV, everything
 * else on purchase×LTV) but returns interest-only carry rather than amortized
 * principal + interest, since principal repayment isn't tax-deductible.
 */
export function monthlyMortgageInterest(
  loanType: LoanScenario | null,
  loan: LoanInputs | null,
  purchase: number,
  arv: number,
): number {
  if (!loanType || !loan) return 0;
  const ltv = Number(loan.ltv) || 0;
  const principal =
    loanType === "brrrr_long_term" ? (arv * ltv) / 100 : (purchase * ltv) / 100;
  return interestOnlyMonthly(principal, Number(loan.rate) || 0);
}

/* ────────────────────────────────────────────────────────────────────────── *
 * The shared per-month breakdown
 * ────────────────────────────────────────────────────────────────────────── */

const OPEX_DEFS: ReadonlyArray<{
  key: keyof CashflowOverrides;
  label: string;
  fallback: (rent: number, purchase: number) => number;
}> = [
  { key: "vacancy", label: "Vacancy", fallback: (r) => r * 0.05 },
  { key: "mgmt", label: "Property management", fallback: (r) => r * 0.08 },
  { key: "taxes", label: "Property taxes", fallback: (_r, p) => p * 0.00103 },
  { key: "insurance", label: "Insurance", fallback: (_r, p) => p * 0.0005 },
  { key: "maint", label: "Maintenance reserve", fallback: (r) => r * 0.08 },
  { key: "capex", label: "Capital reserve (CapEx)", fallback: (r) => r * 0.06 },
  { key: "hoa", label: "HOA fees", fallback: () => 0 },
  { key: "utilities", label: "Utilities", fallback: () => 0 },
];

/**
 * Compute one operating month's breakdown from normalized inputs.
 *
 * `actualMaintenance` is the real maintenance cash spent that month (completed
 * work orders + logged maintenance expenses). Per Rule B, when it's > 0 the
 * actual REPLACES the maintenance reserve for the month (never added on top),
 * so a property in active property management isn't double-charged.
 */
export function computeMonthBreakdown(
  inputs: MonthlyInputs,
  actualMaintenance = 0,
): MonthBreakdown {
  const { rent, purchase, cashflow } = inputs;
  const cfv = (k: keyof CashflowOverrides, fallback: number): number => {
    const raw = cashflow[k];
    return raw === undefined ? fallback : Number(raw) || 0;
  };

  const gross = cfv("gross", rent);
  const other = cfv("other", 0);
  const egi = gross + other;

  const lines: CashflowLine[] = OPEX_DEFS.map(({ key, label, fallback }) => {
    if (key === "maint" && actualMaintenance > 0) {
      // Actual spend replaces the reserve (Rule B — no double-count).
      return { key: "maint", label: "Maintenance (actual)", amount: actualMaintenance };
    }
    return { key, label, amount: cfv(key, fallback(rent, purchase)) };
  });

  const total = lines.reduce((s, l) => s + l.amount, 0);
  // No debt service until the loan has actually started.
  const debt = inputs.loanStarted
    ? monthlyDebtService(inputs.loanType, inputs.loan, purchase, inputs.arv)
    : 0;
  const net = egi - total - debt;

  return {
    income: { gross, other, egi },
    opex: { lines, total },
    debt,
    net,
  };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Normalizers — live settings and frozen log rows → MonthlyInputs
 * ────────────────────────────────────────────────────────────────────────── */

/** Pick the active loan-terms blob from composed settings for a scenario. */
function loanFromSettings(
  loanType: LoanScenario | null,
  settings: SettingsForCashflow | null,
): LoanInputs | null {
  if (!loanType || !settings) return null;
  switch (loanType) {
    case "flip":
      return settings.flip?.hard_money ?? null;
    case "brrrr_hard_money":
      return settings.brrrr?.hard_money ?? null;
    case "brrrr_long_term":
      return settings.brrrr?.long_term ?? null;
    case "rental":
      return settings.rental?.long_term ?? null;
  }
}

/**
 * Per-line operating-expense overrides for a month, applying the rule "an empty
 * `monthly_cash_flow` means zero operating expenses". When the stored overrides
 * object has no keys we return explicit per-line zeros, so the breakdown shows
 * no heuristic estimates — a property whose expenses were never entered (e.g.
 * hand-added via the Add-property wizard) carries no fabricated opex. A
 * non-empty object is returned untouched, so figures entered during
 * underwriting still drive the per-line fallbacks.
 */
const EMPTY_OPEX_ZEROS: CashflowOverrides = {
  vacancy: 0,
  mgmt: 0,
  taxes: 0,
  insurance: 0,
  maint: 0,
  capex: 0,
  hoa: 0,
  utilities: 0,
};

export function resolveCashflowOverrides(
  cf: CashflowOverrides | null | undefined,
): CashflowOverrides {
  return cf && Object.keys(cf).length > 0 ? cf : EMPTY_OPEX_ZEROS;
}

/**
 * Month index of a loan's maturity. Prefers the persisted `maturity_date`;
 * when that's missing (legacy blob, or never saved) it derives maturity from the
 * loan's start date + term — hard-money terms are months, long-term/rental terms
 * are years, matching how {@link monthlyDebtService} reads the term. Returns null
 * when there's no start date or usable term to derive from.
 */
export function maturityMonthKeyFor(
  loan: LoanInputs | null,
  loanType: LoanScenario | null,
): number | null {
  if (!loan) return null;
  const stored = parseMonthKey(loan.maturity_date);
  if (stored !== null) return stored;
  const startKey = parseMonthKey(loan.loan_date);
  if (startKey === null) return null;
  const unit =
    loanType === "rental" || loanType === "brrrr_long_term" ? "years" : "months";
  const months = termInMonths(loan, unit);
  if (!Number.isFinite(months) || months <= 0) return null;
  return startKey + months;
}

/** Live current-month inputs from the mutable property_setting + loan switcher. */
export function normalizeFromSettings(args: {
  strategy: Strategy;
  settings: SettingsForCashflow | null;
  loan: LoanSwitch | null;
  /** Active rent (rental_listings.rent_amount); falls back to monthly_rent. */
  rentAmount: number | null;
  /** Reference "now" for the loan-started check (injectable for tests). */
  now?: Date;
}): MonthlyInputs {
  const { strategy, settings } = args;
  const loanType = resolveLoanScenario(strategy, args.loan);
  const purchase = Number(settings?.purchase_price) || 0;
  const arv = Number(settings?.after_repair_value) || Math.round(purchase * 1.28);
  const rent =
    Number(args.rentAmount) || Number(settings?.monthly_rent) || 0;
  const loan = loanFromSettings(loanType, settings);
  // Debt service runs only inside the loan's window: an enabled loan whose
  // loan_date is on/before the current month AND whose maturity month hasn't
  // passed. After maturity the loan is paid off / ballooned, so no payment.
  const enabled = args.loan?.is_enabled === true;
  const currentKey = currentMonthKeyUTC(args.now);
  const startKey = parseMonthKey(loan?.loan_date);
  const maturityKey = maturityMonthKeyFor(loan, loanType);
  const loanStarted =
    enabled &&
    startKey !== null &&
    startKey <= currentKey &&
    (maturityKey === null || currentKey <= maturityKey);
  // Flip carrying-cost estimate for the live month, mirroring the underwriting
  // defaults used by the pipeline Flip Analysis when a value isn't stored.
  const flipHolding =
    strategy === "Flip"
      ? {
          taxes: Number(settings?.flip?.taxes_monthly ?? Math.round(purchase * 0.0018)),
          utilities: Number(settings?.flip?.utilities_monthly ?? 100),
        }
      : null;
  return {
    strategy,
    loanType,
    purchase,
    arv,
    rent,
    cashflow: resolveCashflowOverrides(settings?.monthly_cash_flow),
    loan,
    loanStarted,
    flipHolding,
  };
}

/** Row shape read back from `property_log`. JSONB columns arrive as objects. */
export interface PropertyLogRow {
  pipeline_property_id: string;
  year: number;
  month: number;
  month_name: string;
  loan_type: LoanScenario | null;
  /** Whether the loan had started by this frozen month. */
  loan_started: boolean | null;
  purchase_price: number | null;
  after_repair_value: number | null;
  monthly_rent: number | null;
  rent_amount: number | null;
  monthly_cash_flow: CashflowOverrides | null;
  flip_hard_money: LoanInputs | null;
  /** Frozen flip carrying-cost estimate for this month (taxes+ins · utilities). */
  flip_holding_cost: { taxes_monthly?: number; utilities_monthly?: number } | null;
  brrrr_hard_money: LoanInputs | null;
  brrrr_long_term: LoanInputs | null;
  rental_long_term: LoanInputs | null;
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Assemble a property's trailing monthly series (past = log, current = live,
 * future = excluded)
 * ────────────────────────────────────────────────────────────────────────── */

/** One resolved month for the Properties view. */
export interface MonthlyResult {
  year: number;
  /** 1-12. */
  month: number;
  monthName: string;
  /** Where the figures came from — a frozen snapshot or the live current month. */
  source: "log" | "live";
  breakdown: MonthBreakdown;
}

/** Split a month index back into { year, month1to12 }. */
export function monthKeyToParts(key: number): { year: number; month: number } {
  return { year: Math.floor(key / 12), month: (key % 12) + 1 };
}

/** "YYYY-MM-DD" → month index, timezone-safe (string slice, no Date). */
export function parseMonthKey(dateStr: string | null | undefined): number | null {
  if (!dateStr || dateStr.length < 7) return null;
  const year = Number(dateStr.slice(0, 4));
  const month = Number(dateStr.slice(5, 7));
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  return monthKey(year, month);
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Full calendar-year series with lifecycle phases (powers the detail view)
 * ────────────────────────────────────────────────────────────────────────── */

export type Phase = "pre" | "rehab" | "hold" | "operating" | "future" | "sold";

export interface YearMonth {
  year: number;
  month: number;
  monthName: string;
  phase: Phase;
  /** "log" | "live" for months with figures; null for pre/future. */
  source: "log" | "live" | null;
  /** Full breakdown for operating/rehab months; null for pre/future. */
  breakdown: MonthBreakdown | null;
  /** One-time rehab capital landing in this month (completion month). */
  rehabSpend: number;
  /** net = breakdown.net − rehabSpend (rehab capital is an outflow). */
  net: number;
}

/**
 * A rehab/vacant month: no rent, holding costs only, interest-only debt.
 *
 * Surfaces the full operating-expense line set (the same eight as an operating
 * month) so the holding-cost breakdown reads consistently across phases. The
 * unit isn't rented during rehab, so the rent-based reserves (vacancy,
 * management, maintenance, capex) are computed against rent = 0 and read $0
 * unless an explicit override is present; the fixed carrying costs (taxes,
 * insurance, HOA, utilities) still apply via their purchase-based / override
 * fallbacks.
 */
function vacantBreakdown(inputs: MonthlyInputs): MonthBreakdown {
  const { purchase, cashflow } = inputs;
  const cfv = (k: keyof CashflowOverrides, fallback: number): number => {
    const raw = cashflow[k];
    return raw === undefined ? fallback : Number(raw) || 0;
  };
  const lines: CashflowLine[] = OPEX_DEFS.map(({ key, label, fallback }) => ({
    key,
    label,
    amount: cfv(key, fallback(0, purchase)),
  }));
  const total = lines.reduce((s, l) => s + l.amount, 0);
  // Interest-only carry on the acquisition loan — only once the loan has started.
  const ltv = Number(inputs.loan?.ltv) || 0;
  const rate = Number(inputs.loan?.rate) || 0;
  const debt = inputs.loanStarted && inputs.loan ? ((purchase * ltv) / 100) * (rate / 100 / 12) : 0;
  return {
    income: { gross: 0, other: 0, egi: 0 },
    opex: { lines, total },
    debt,
    net: -(total + debt),
  };
}

/**
 * A Flip holding month: no income and NO fabricated rental reserves — a flip is
 * carried, not rented. Its recurring carrying cost is the flip holding estimate
 * (taxes+ins · utilities) for THAT month — sourced per-month via `flipHolding`
 * (live settings for the current month, frozen `property_log.flip_holding_cost`
 * for past months), so editing today's assumptions never rewrites history. Only
 * the taxes + utilities opex lines carry a value; the six rental reserves stay
 * $0. Debt service is the acquisition loan carry.
 *
 * Actual one-off carrying costs (a specific tax bill, a utility invoice) are
 * layered on top via the logged-expense overlay.
 */
function flipHoldingBreakdown(inputs: MonthlyInputs): MonthBreakdown {
  const taxes = Number(inputs.flipHolding?.taxes) || 0;
  const utilities = Number(inputs.flipHolding?.utilities) || 0;
  const lines: CashflowLine[] = OPEX_DEFS.map(({ key, label }) => ({
    key,
    label,
    amount: key === "taxes" ? taxes : key === "utilities" ? utilities : 0,
  }));
  const total = taxes + utilities;
  const debt = inputs.loanStarted
    ? monthlyDebtService(inputs.loanType, inputs.loan, inputs.purchase, inputs.arv)
    : 0;
  return {
    income: { gross: 0, other: 0, egi: 0 },
    opex: { lines, total },
    debt,
    net: -(total + debt),
  };
}

/**
 * Distribute rehab line-item spend across calendar months by each item's
 * `completed_at`, returning a Map keyed by month index (see {@link monthKey}).
 *
 * An item with spend but no parseable completion date (a completed row whose
 * date was cleared, or a legacy row) falls back to `fallbackMonthKey` — the
 * rehab completion month — so no rehab dollars go missing from the year.
 * `completed_at` is a full ISO timestamp; {@link parseMonthKey} reads only its
 * leading `YYYY-MM`, so the timestamp's time-of-day / offset is irrelevant.
 */
export function rehabSpendByCompletedMonth(
  items: ReadonlyArray<{
    actual_cost: number | null;
    completed_at: string | null;
  }>,
  fallbackMonthKey: number | null,
): Map<number, number> {
  const byMonth = new Map<number, number>();
  for (const it of items) {
    const spend = Number(it.actual_cost) || 0;
    if (spend <= 0) continue;
    const key = parseMonthKey(it.completed_at) ?? fallbackMonthKey;
    if (key === null) continue;
    byMonth.set(key, (byMonth.get(key) ?? 0) + spend);
  }
  return byMonth;
}

/** Budget-vs-spend figures for the property detail Rehab card. */
export interface RehabBudgetView {
  /** Budget to measure spend against. */
  estimate: number;
  /** Spend to date. */
  actual: number;
  /** `actual / estimate` as 0-100, clamped. 0 when nothing is budgeted. */
  progressPct: number;
  /** True only when spend exceeds budget by more than rounding noise. */
  overBudget: boolean;
}

/**
 * Rehab budget vs. spend for the property detail card's progress bar.
 *
 * Reads the LIVE scope rows rather than the project roll-up, because
 * `rehab_projects.total_budget` / `total_spent` are only stamped when a project
 * is marked complete — an in-progress rehab reports a frozen (usually zero)
 * actual there, so a bar driven by it never moves as items are finished.
 *
 * Falls back to the project roll-up (then the underwriting repair estimate)
 * only when nothing is scoped, so a rehab tracked without line items still
 * shows its figures. Same "prefer line items, fall back to project totals"
 * rule as `deriveBudget` in `components/rehab/rehab-status.ts`.
 *
 * Spend sums `actual_cost` across rows whose `status` is `completed` — money
 * counts once the work it paid for is finished. Same rule as `recognizedSpend`
 * in `components/rehab/rehab-scope.ts` (the Rehab page card and drawer) and as
 * the accounting expense feed, so an amount recorded against a task that isn't
 * checked off yet never moves this bar.
 *
 * NOTE: {@link rehabSpendByCompletedMonth} still buckets spend from every row,
 * so a pending row carrying an amount can make the cash-flow panel's yearly
 * rehab total exceed the "Actual spend" shown here.
 */
export function deriveRehabBudget(
  items: ReadonlyArray<{
    estimated_cost: number;
    actual_cost: number | null;
    status?: string | null;
  }>,
  fallback: {
    projectEstimated?: number | null;
    projectActual?: number | null;
    repairCostEstimate?: number | null;
  } = {},
): RehabBudgetView {
  let lineEstimate = 0;
  let lineActual = 0;
  for (const it of items) {
    // Budget spans the whole scope; spend counts completed rows only.
    lineEstimate += Number(it.estimated_cost) || 0;
    if (it.status === "completed") lineActual += Number(it.actual_cost) || 0;
  }
  // A scope of $0 placeholders is not a budget — fall back rather than pin the
  // bar at 0% against a zero estimate.
  const scoped = lineEstimate > 0;
  const estimate = scoped
    ? lineEstimate
    : Number(fallback.projectEstimated) ||
      Number(fallback.repairCostEstimate) ||
      0;
  const actual = scoped ? lineActual : Number(fallback.projectActual) || 0;
  return {
    estimate,
    actual,
    progressPct:
      estimate > 0 ? Math.min(100, Math.max(0, (actual / estimate) * 100)) : 0,
    overBudget: actual - estimate > 0.5,
  };
}

/** One named rehab expense (a completed line item's label + actual spend). */
export interface RehabSpendItem {
  label: string;
  amount: number;
}

/**
 * Like {@link rehabSpendByCompletedMonth}, but keeps each line item itemized
 * (label + actual spend) instead of summing to a single figure — so the
 * breakdown drawer can list rehab spend by task name rather than one lump.
 * Uses the same bucketing rules (keyed by the month of `completed_at`, falling
 * back to `fallbackMonthKey`; rows with no spend skipped) so the itemized rows
 * always sum to the month's rehab total.
 */
export function rehabItemsByCompletedMonth(
  items: ReadonlyArray<{
    label: string | null;
    actual_cost: number | null;
    completed_at: string | null;
  }>,
  fallbackMonthKey: number | null,
): Map<number, RehabSpendItem[]> {
  const byMonth = new Map<number, RehabSpendItem[]>();
  for (const it of items) {
    const spend = Number(it.actual_cost) || 0;
    if (spend <= 0) continue;
    const key = parseMonthKey(it.completed_at) ?? fallbackMonthKey;
    if (key === null) continue;
    const entry = { label: it.label ?? "Rehab item", amount: spend };
    const list = byMonth.get(key);
    if (list) list.push(entry);
    else byMonth.set(key, [entry]);
  }
  return byMonth;
}

/**
 * Resolve the month a property's rehab actually ended, from its line items
 * rather than a manual "marked complete" flag.
 *
 * A line item counts as finished only when its `status` is `"completed"` AND it
 * carries a `completed_at` date — a `pending` row (or a completed row whose date
 * was cleared) keeps the rehab open.
 *
 *   - No scoped line items (none with a budget) → fall back to `movedAt`, the
 *     project-level completion signal, so projects tracked without line items
 *     still get a boundary.
 *   - Any scoped item still unfinished → rehab is in progress; keep `movedAt`
 *     (null while genuinely ongoing).
 *   - Every scoped item finished → rehab ends at the LATEST completion date, so
 *     months after the last task completed read as operating even if the rehab
 *     was "marked complete" in a later month.
 *
 * `completed_at` values are ISO timestamps, which sort lexicographically, so a
 * string comparison finds the latest without constructing Date objects.
 */
export function effectiveRehabEnd(
  items: ReadonlyArray<{
    estimated_cost: number;
    status: string | null;
    completed_at: string | null;
  }>,
  movedAt: string | null,
): string | null {
  const scoped = items.filter((i) => Number(i.estimated_cost) > 0);
  if (scoped.length === 0) return movedAt;
  const isDone = (i: (typeof scoped)[number]) =>
    i.status === "completed" && Boolean(i.completed_at);
  if (scoped.some((i) => !isDone(i))) return movedAt;
  let latest: string | null = null;
  for (const i of scoped) {
    if (i.completed_at && (latest === null || i.completed_at > latest)) {
      latest = i.completed_at;
    }
  }
  return latest ?? movedAt;
}

/**
 * Build all 12 months of `year` with lifecycle phases. Past months come from
 * frozen logs, the current month is live, future months are excluded (phase
 * "future", no figures). Pre-acquisition months are phase "pre".
 */
export function buildYearMonths(args: {
  year: number;
  strategy: Strategy;
  logs: PropertyLogRow[];
  live: MonthlyInputs | null;
  /** "YYYY-MM-DD" — rehab start (acquisition). */
  acquisitionDate: string | null;
  /** "YYYY-MM-DD" — rehab completion (null while still in rehab). */
  rehabEndDate: string | null;
  /** Total actual rehab spend, attributed to the completion month. Used only
   * as the fallback when `rehabSpendByMonth` is not supplied. */
  rehabActual: number;
  /**
   * Per-month rehab spend keyed by month index (from
   * {@link rehabSpendByCompletedMonth}). When provided, each month draws its
   * rehab outflow from this map instead of lumping `rehabActual` into the
   * completion month.
   */
  rehabSpendByMonth?: Map<number, number>;
  /**
   * Flip mode: every owned month is a HOLDING month, never "operating". A flip
   * is carried (not rented), so no income and no rental reserves are fabricated
   * — each month's modeled cost is debt service only, with actual carrying costs
   * arriving via the logged-expense overlay. Months up to rehab completion stay
   * phase "rehab"; the post-rehab carry-to-sale window is phase "hold".
   */
  holdingOnly?: boolean;
  /**
   * "YYYY-MM-DD" sale (close) date. Once sold, the property produces no further
   * cash flow — months AFTER the sale month carry no figures (phase "sold"), so
   * the panels stop projecting/showing calculations for post-sale months. The
   * sale month itself still shows, since the property was owned through it.
   */
  saleDate?: string | null;
  now?: Date;
}): YearMonth[] {
  const now = args.now ?? new Date();
  const currentKey = currentMonthKeyUTC(now);
  const acqKey = parseMonthKey(args.acquisitionDate);
  const rehabEndKey = parseMonthKey(args.rehabEndDate);
  const saleKey = parseMonthKey(args.saleDate);
  const logByKey = new Map<number, PropertyLogRow>();
  for (const row of args.logs) logByKey.set(monthKey(row.year, row.month), row);

  // Rehab outflow for a month: the per-completion-month distribution when
  // supplied, else the legacy lump into the completion month.
  const spendFor = (key: number): number =>
    args.rehabSpendByMonth
      ? args.rehabSpendByMonth.get(key) ?? 0
      : rehabEndKey !== null && key === rehabEndKey
        ? args.rehabActual
        : 0;

  const out: YearMonth[] = [];
  for (let month = 1; month <= 12; month += 1) {
    const key = monthKey(args.year, month);
    const base = {
      year: args.year,
      month,
      monthName: MONTH_NAMES[month - 1],
      rehabSpend: 0,
    };

    if (key > currentKey) {
      out.push({ ...base, phase: "future", source: null, breakdown: null, net: 0 });
      continue;
    }
    // Post-sale: no cash flow after the sale month — don't compute or show it.
    if (saleKey !== null && key > saleKey) {
      out.push({ ...base, phase: "sold", source: null, breakdown: null, net: 0 });
      continue;
    }
    if (acqKey !== null && key < acqKey) {
      out.push({ ...base, phase: "pre", source: null, breakdown: null, net: 0 });
      continue;
    }

    // Resolve inputs (live for current month, else the frozen snapshot).
    let inputs: MonthlyInputs | null = null;
    let source: "log" | "live" | null = null;
    if (key === currentKey && args.live) {
      inputs = args.live;
      source = "live";
    } else {
      const row = logByKey.get(key);
      if (row) {
        inputs = normalizeFromLog(row, args.strategy);
        source = "log";
      }
    }

    const rehabSpend = spendFor(key);
    // A month is "rehab" when a rehab line item actually completed in it — i.e.
    // it carries completed rehab spend. Months with no rehab activity stay
    // operating regardless of whether the project as a whole is finished, so
    // completing the last task never retroactively repaints earlier months
    // that had none. Flips are the exception: a flip is carried (never rented),
    // so its whole acquisition→completion window reads "rehab" and the
    // post-completion carry-to-sale window reads "hold".
    const isRehab = args.holdingOnly
      ? rehabEndKey !== null && key <= rehabEndKey
      : rehabSpend > 0;

    if (!inputs) {
      // No figures for this month (no live inputs, no frozen snapshot). Still
      // surface any rehab spend attributed here so distributed dollars aren't
      // silently dropped in a rehab month that was never snapshotted.
      out.push({
        ...base,
        phase:
          acqKey === null
            ? "pre"
            : isRehab
              ? "rehab"
              : args.holdingOnly
                ? "hold"
                : "operating",
        source: null,
        breakdown: null,
        rehabSpend,
        net: -rehabSpend,
      });
      continue;
    }

    // Flip: hold-only (income-free carry). Others: rehab months are vacant
    // holding, later months are stabilized operating.
    const breakdown = args.holdingOnly
      ? flipHoldingBreakdown(inputs)
      : isRehab
        ? vacantBreakdown(inputs)
        : computeMonthBreakdown(inputs);
    out.push({
      ...base,
      phase: isRehab ? "rehab" : args.holdingOnly ? "hold" : "operating",
      source,
      breakdown,
      rehabSpend,
      net: breakdown.net - rehabSpend,
    });
  }
  return out;
}

/**
 * Deductible mortgage interest for each month of `year`, keyed by month index
 * (see {@link monthKey}). Mirrors {@link buildYearMonths}' month resolution —
 * the current month is live from `property_setting` + the loans switch, past
 * months are read from frozen `property_log` snapshots, and future /
 * pre-acquisition / post-sale months are excluded — but records only the
 * interest portion ({@link monthlyMortgageInterest}) for months in which the
 * loan was actually active (`loanStarted`). Powers the accounting hook's
 * Schedule E line 12 without recomputing a full cash-flow breakdown.
 */
export function mortgageInterestByMonth(args: {
  year: number;
  strategy: Strategy;
  logs: PropertyLogRow[];
  live: MonthlyInputs | null;
  /** "YYYY-MM-DD" — acquisition date; months before it carry no debt. */
  acquisitionDate: string | null;
  /** "YYYY-MM-DD" — sale date; months after it carry no debt. */
  saleDate?: string | null;
  now?: Date;
}): Map<number, number> {
  const now = args.now ?? new Date();
  const currentKey = currentMonthKeyUTC(now);
  const acqKey = parseMonthKey(args.acquisitionDate);
  const saleKey = parseMonthKey(args.saleDate);
  const logByKey = new Map<number, PropertyLogRow>();
  for (const row of args.logs) logByKey.set(monthKey(row.year, row.month), row);

  const out = new Map<number, number>();
  for (let month = 1; month <= 12; month += 1) {
    const key = monthKey(args.year, month);
    if (key > currentKey) continue; // future
    if (saleKey !== null && key > saleKey) continue; // post-sale
    if (acqKey !== null && key < acqKey) continue; // pre-acquisition

    let inputs: MonthlyInputs | null = null;
    if (key === currentKey && args.live) {
      inputs = args.live;
    } else {
      const row = logByKey.get(key);
      if (row) inputs = normalizeFromLog(row, args.strategy);
    }
    if (!inputs || !inputs.loanStarted) continue;

    const interest = monthlyMortgageInterest(
      inputs.loanType,
      inputs.loan,
      inputs.purchase,
      inputs.arv,
    );
    if (interest > 0) out.set(key, interest);
  }
  return out;
}

/** One point on the Monthly performance chart. */
export interface PerformancePoint {
  month: string;
  rent: number;
  expenses: number;
  cashflow: number;
}

/**
 * Aggregate several properties' monthly series into one chart series, aligned
 * by calendar month. `expenses = opex + debt`, `rent = EGI`, `cashflow = net`,
 * so the chart stays internally consistent (rent − expenses = cashflow).
 */
export function aggregatePerformanceSeries(
  perProperty: MonthlyResult[][],
): PerformancePoint[] {
  const byKey = new Map<number, PerformancePoint & { _key: number }>();
  for (const months of perProperty) {
    for (const m of months) {
      const key = monthKey(m.year, m.month);
      const point = byKey.get(key) ?? {
        _key: key,
        month: MONTH_ABBR[m.month - 1],
        rent: 0,
        expenses: 0,
        cashflow: 0,
      };
      point.rent += m.breakdown.income.egi;
      point.expenses += m.breakdown.opex.total + m.breakdown.debt;
      point.cashflow += m.breakdown.net;
      byKey.set(key, point);
    }
  }
  return [...byKey.values()]
    .sort((a, b) => a._key - b._key)
    .map(({ _key: _drop, ...point }) => point);
}

/**
 * Build the trailing-window monthly series for one property.
 *
 * Past months come from frozen `property_log` rows; the current month is
 * computed live from `live` inputs. Future months are never produced. Months in
 * the window with neither a log row nor (for the current month) live inputs are
 * omitted rather than fabricated.
 */
export function buildPropertyMonths(args: {
  strategy: Strategy;
  logs: PropertyLogRow[];
  /** Current-month live inputs, or null when unavailable. */
  live: MonthlyInputs | null;
  /** Actual maintenance cash per "YYYY-MM" (completed work orders + logged). */
  actualMaintenanceByMonth?: Record<string, number>;
  /** Trailing months to include (default 12). */
  monthsBack?: number;
  now?: Date;
}): MonthlyResult[] {
  const monthsBack = args.monthsBack ?? 12;
  const now = args.now ?? new Date();
  const currentKey = currentMonthKeyUTC(now);
  const logByKey = new Map<number, PropertyLogRow>();
  for (const row of args.logs) logByKey.set(monthKey(row.year, row.month), row);

  const out: MonthlyResult[] = [];
  for (let key = currentKey - (monthsBack - 1); key <= currentKey; key += 1) {
    const { year, month } = monthKeyToParts(key);
    const ym = `${year}-${String(month).padStart(2, "0")}`;
    const actualMaint = args.actualMaintenanceByMonth?.[ym] ?? 0;

    let inputs: MonthlyInputs | null = null;
    let source: "log" | "live" = "log";
    if (key === currentKey && args.live) {
      inputs = args.live;
      source = "live";
    } else {
      const row = logByKey.get(key);
      if (row) inputs = normalizeFromLog(row, args.strategy);
    }
    if (!inputs) continue;

    out.push({
      year,
      month,
      monthName: MONTH_NAMES[month - 1],
      source,
      breakdown: computeMonthBreakdown(inputs, actualMaint),
    });
  }
  return out;
}

/** Frozen past-month inputs from a property_log snapshot. */
export function normalizeFromLog(row: PropertyLogRow, strategy: Strategy): MonthlyInputs {
  const purchase = Number(row.purchase_price) || 0;
  const arv = Number(row.after_repair_value) || Math.round(purchase * 1.28);
  const rent = Number(row.rent_amount) || Number(row.monthly_rent) || 0;
  const loan =
    row.loan_type === "flip"
      ? row.flip_hard_money
      : row.loan_type === "brrrr_hard_money"
        ? row.brrrr_hard_money
        : row.loan_type === "brrrr_long_term"
          ? row.brrrr_long_term
          : row.loan_type === "rental"
            ? row.rental_long_term
            : null;
  // Frozen flip carrying cost for past months — read straight from the snapshot,
  // never from current settings, so history can't be rewritten. No heuristic
  // fallback: an old snapshot without the field carries $0 holding.
  const flipHolding =
    row.loan_type === "flip"
      ? {
          taxes: Number(row.flip_holding_cost?.taxes_monthly) || 0,
          utilities: Number(row.flip_holding_cost?.utilities_monthly) || 0,
        }
      : null;
  return {
    strategy,
    loanType: row.loan_type,
    purchase,
    arv,
    rent,
    cashflow: resolveCashflowOverrides(row.monthly_cash_flow),
    loan: loan ?? null,
    // Frozen at snapshot time — past months never recompute loan-started.
    loanStarted: row.loan_started === true,
    flipHolding,
  };
}
